import { useState, useEffect, useContext } from "react";
import { useSession } from "next-auth/react";
import { Text, Center, Box, Button, useColorModeValue } from "@chakra-ui/react";
import PuffLoader from "react-spinners/PuffLoader";
import { useFirebaseUser } from "utils/useFirebaseUser";
import { firebaseClient } from "firebaseClient";
import { getAccount } from "utils/sessionHelper";
import { isTweetFromAccount } from "controllers/schedule";
import { TweetBar } from "./tweet-bar";
import { textStyle } from "theme/names";
import { colors } from "theme/colors/colors";

interface Props {
  isSelected: boolean;
}

export function ErrorsPanel({ isSelected }: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorPosts, seterrorPosts] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const isAuthenticated = useFirebaseUser();
  const { data: session } = useSession() ?? {}
  const tweetbarBg = useColorModeValue("blue.50", "blue.900");

  useEffect(() => {
    if (isAuthenticated && isSelected) {
      loaderrorPosts();
    }
  }, [isAuthenticated, isSelected]);

  const loaderrorPosts = async () => {
    setIsLoading(true);
    await geterrorPosts();
    setIsLoading(false);
  }

  const geterrorPosts = async (type?: "loadMore") => {
    if (!isAuthenticated) {
      return;
    }

    const LOAD_BATCH = 30;

    let errorPostsCopy = type === "loadMore" ? [...errorPosts] : [];
    const db = firebaseClient.firestore();

    let query = db
      .collection("users")
      .doc(getAccount(session).id)
      .collection("tweets")
      .where("status", "==", "draft")
      .where("isError", "==", true)
      .where("idAccount", "==", getAccount(session).idAccount)
      .orderBy("dateCreated", "desc");

    if (type === "loadMore" && errorPostsCopy.length > 0) {
      query = query.startAfter(errorPostsCopy[errorPostsCopy?.length - 1]?.dateCreated);
    }

    const docs = await query.limit(LOAD_BATCH).get();

    docs.forEach((doc) => {
      const data = doc.data();
      if (isTweetFromAccount(data, session)) {
        if (data.text === "") {
          db.collection("users")
            .doc(getAccount(session).id)
            .collection("tweets")
            .doc(doc.id)
            .delete();
        } else {
          errorPostsCopy.push(data);
        }
      }
    });

    seterrorPosts(errorPostsCopy);

    if (docs?.docs?.length === LOAD_BATCH) {
      setHasMore(true);
    } else {
      setHasMore(false);
    }
  };

  const onDelete = async (docId: string) => {
    const filterederrorPosts = errorPosts.filter((d) => d.id !== docId);
    seterrorPosts(filterederrorPosts);
  };

  const loadMoreErrorPosts = async () => {
    if (!isAuthenticated) {
      return;
    }

    setIsLoadingMore(true);
    await geterrorPosts("loadMore");
    setIsLoadingMore(false);
  }

  const onAddToQueue = async (docId: string) => {
    const filterederrorPosts = errorPosts.filter((d) => d.id !== docId);
    seterrorPosts(filterederrorPosts);
  }

  if (isLoading) {
    return (
      <Center mt="40">
        <PuffLoader
          color={"#09527c"}
          loading={isLoading}
          cssOverride={{ display: "block", margin: "0 auto", borderColor: "red" }}
          size={50}
        />
      </Center>
    );
  }

  if (!isLoading && errorPosts.length === 0) {
    return (
      <Center mt="44">
        <Box textAlign="center">
          <Text textStyle={textStyle["body.medium.light"]}>No errors found</Text>
        </Box>
      </Center>
    );
  }

  return (
    <Box>
      <Text textStyle={textStyle["body.medium.light"]} pb={4} mt="7" fontSize="lg" fontWeight="700" style={{ color: colors.alert.lightMode.default }}>
        My Errors
      </Text>
      {errorPosts.map((errorPost) => (
        <TweetBar
          key={errorPost.id}
          type="errors"
          tweet={errorPost}
          bg={tweetbarBg}
          onDelete={onDelete}
          onAddToQueue={onAddToQueue}
        />
      ))}
      {errorPosts.length > 0 && hasMore && (
        <Box textAlign="center" mt="16">
          <Button
            variant="secondary"
            size="sm"
            onClick={loadMoreErrorPosts}
            isLoading={isLoadingMore}
          >
            Load more
          </Button>
        </Box>
      )}
    </Box>
  );
}